import { useState, useMemo, useCallback, useEffect } from 'react';
import i18n from 'i18next';
import useStateWithLocalStorage from '../helpers/useStateWithLocalStorage';
import { createCloudinaryWidget } from '../utils/cloudinaryConfig';

export default function useAdminImageUpload(documentId, token, onImageUpdate, uploadFolder) {
  const currentLanguageCode = i18n.language || 'en';
  const [isUploadingFront, setIsUploadingFront] = useState(false);
  const [isUploadingBack, setIsUploadingBack] = useState(false);
  const [clipboardTarget, setClipboardTarget] = useState(null);

  const updateDocumentImage = useCallback(async (imageAttribute, imageUrl) => {
    const updateFields = {};
    updateFields[imageAttribute] = imageUrl;
    const settings = {
      method: 'POST',
      headers: {
          Accept: 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
      },
      body: JSON.stringify({_id: documentId, ...updateFields})
    };

    const response = await fetch(
      `${process.env.REACT_APP_API_SERVER_URL}/api/documents`,
      settings,
    );

    if (response.ok) {
      const updatedDoc = await response.json();
      onImageUpdate(updatedDoc);
    } else {
      console.error('Failed to update document with new image');
    }
  }, [documentId, token, onImageUpdate]);

  const uploadClipboardImage = useCallback(async (imageFile, imageAttribute, setUploading) => {
    if (!imageFile) {
      return;
    }

    const formData = new FormData();
    formData.append('file', imageFile);
    formData.append('upload_preset', 'fz7jch0p');
    formData.append('folder', uploadFolder || 'admin_uploads');
    setUploading(true);

    try {
      const response = await fetch('https://api.cloudinary.com/v1_1/tidp/image/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload clipboard image');
      }

      const uploadResult = await response.json();
      await updateDocumentImage(imageAttribute, uploadResult.secure_url);
    } catch (error) {
      console.error(error);
    } finally {
      setUploading(false);
      setClipboardTarget(null);
    }
  }, [updateDocumentImage, uploadFolder]);

  const handlePaste = useCallback((event) => {
    if (!clipboardTarget) {
      return;
    }

    const clipboardItems = Array.from(event.clipboardData?.items || []);

    const clipboardFile = clipboardItems
      .map((item) => item.kind === 'file' ? item.getAsFile() : null)
      .find((file) => file && file.type.startsWith('image/'));

    if (clipboardFile) {
      event.preventDefault();

      if (clipboardTarget === 'front') {
        uploadClipboardImage(
          clipboardFile,
          'nationalLicenseAttributes.frontImageUrl',
          setIsUploadingFront,
        );
      }

      if (clipboardTarget === 'back') {
        uploadClipboardImage(
          clipboardFile,
          'nationalLicenseAttributes.backImageUrl',
          setIsUploadingBack,
        );
      }
      return;
    }

    const textItem = clipboardItems.find((item) => item.kind === 'string' && item.type === 'text/plain');
    if (textItem) {
      textItem.getAsString((text) => {
        const trimmed = text.trim();
        let isUrl = false;
        try {
          const parsed = new URL(trimmed);
          isUrl = parsed.protocol === 'http:' || parsed.protocol === 'https:';
        } catch (_) {
          isUrl = false;
        }

        if (!isUrl) {
          return;
        }

        event.preventDefault();

        if (clipboardTarget === 'front') {
          updateDocumentImage('nationalLicenseAttributes.frontImageUrl', trimmed)
            .finally(() => setClipboardTarget(null));
        }

        if (clipboardTarget === 'back') {
          updateDocumentImage('nationalLicenseAttributes.backImageUrl', trimmed)
            .finally(() => setClipboardTarget(null));
        }
      });
    }
  }, [clipboardTarget, uploadClipboardImage, updateDocumentImage]);

  useEffect(() => {
    if (!clipboardTarget) {
      return undefined;
    }

    window.addEventListener('paste', handlePaste);

    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [clipboardTarget, handlePaste]);

  const startClipboardPaste = useCallback((target) => {
    setClipboardTarget(target);
  }, []);

  const cancelClipboardPaste = useCallback(() => {
    setClipboardTarget(null);
  }, []);

const frontImageWidget = useMemo(() => {
    const callback = async (error, result) => {
      if (!error && result && result.event === 'success') {
        console.log('Front image uploaded:', result.info);
        setIsUploadingFront(true);
        await updateDocumentImage('nationalLicenseAttributes.frontImageUrl', result.info.secure_url);
        setIsUploadingFront(false);
      }
    };

    return createCloudinaryWidget({
      language: currentLanguageCode,
      folder: uploadFolder || 'admin_uploads',
      callback,
    });
  }, [updateDocumentImage, currentLanguageCode, uploadFolder]);

  const backImageWidget = useMemo(() => {
    const callback = async (error, result) => {
      if (!error && result && result.event === 'success') {
        console.log('Back image uploaded:', result.info);
        setIsUploadingBack(true);
        await updateDocumentImage('nationalLicenseAttributes.backImageUrl', result.info.secure_url);
        setIsUploadingBack(false);
      }
    };

    return createCloudinaryWidget({
      language: currentLanguageCode,
      folder: uploadFolder || 'admin_uploads',
      callback,
    });
  }, [updateDocumentImage, currentLanguageCode, uploadFolder]);

  return {
    isUploadingFront,
    isUploadingBack,
    frontImageWidget,
    backImageWidget,
    startClipboardPaste,
    cancelClipboardPaste,
    clipboardTarget,
  };
}